import React from 'react'
import { Socket } from 'socket.io-client'
import { useLocation } from 'react-router-dom'
import {
  IChatMessageCreatedPayload,
  ISupportChatRemovedPayload,
  ISupportChatUpdatedPayload,
  ISupportUnreadCounters
} from 'models/chat'
import { TOKEN } from 'shared/constants/app'
import { superAdminRoutes } from 'shared/constants/routes'
import { createChatSocket } from 'shared/utils/chatSocket'
import { showBrowserNotification } from 'shared/utils/notifications'
import { getChatAuthorName, getMessagePreview } from 'components/chats/utils'
import {
  applySupportChatRemoved,
  getSupportCounters,
  supportActions,
  syncSupportChats
} from 'store/support'
import { useAppDispatch } from './useReduxHooks'

const NOTIFICATION_TAG = 'horsesmart-support'

// Счётчики обращений в поддержку и браузерные уведомления админу.
// Хук зовём из обёртки приложения: она смонтирована всегда
export const useSupportRealtime = (isEnabled: boolean) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const socketRef = React.useRef<Socket | null>(null)
  const pathnameRef = React.useRef(location.pathname)

  React.useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  React.useEffect(() => {
    if (!isEnabled) return undefined

    dispatch(getSupportCounters())

    if (!localStorage.getItem(TOKEN)) return undefined

    const socket = createChatSocket()
    socketRef.current = socket

    // Переподключение после обрыва: события, пришедшие без нас, потеряны — перечитываем
    let hasConnected = false
    socket.on('connect', () => {
      if (hasConnected) {
        dispatch(getSupportCounters())
        if (pathnameRef.current.startsWith(superAdminRoutes.SUPPORT)) {
          dispatch(syncSupportChats())
        }
      }
      hasConnected = true
    })
    socket.on('support_unread_count_updated', (payload: ISupportUnreadCounters) => {
      dispatch(supportActions.setCounters(payload))
    })
    socket.on('support_chat_updated', (payload: ISupportChatUpdatedPayload) => {
      dispatch(supportActions.applyChatUpdated(payload))
    })
    socket.on('support_chat_removed', (payload: ISupportChatRemovedPayload) => {
      dispatch(applySupportChatRemoved(payload))
    })
    socket.on('support_message_updated', (payload: IChatMessageCreatedPayload) => {
      dispatch(supportActions.applyMessageUpdated(payload))
    })
    socket.on('support_message_created', (payload: IChatMessageCreatedPayload) => {
      dispatch(supportActions.applyMessageCreated(payload))

      const isFromVisitor = payload.message.sender_type === 'USER'
      if (!isFromVisitor) return

      // Раздел поддержки открыт на экране — уведомление только помешает
      const isSupportOpen =
        document.visibilityState === 'visible' &&
        pathnameRef.current.startsWith(superAdminRoutes.SUPPORT)
      if (isSupportOpen) return

      void showBrowserNotification({
        title: `Поддержка: ${getChatAuthorName(payload.chat)}`,
        body: getMessagePreview(payload.message),
        tag: NOTIFICATION_TAG,
        url: superAdminRoutes.SUPPORT
      })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [dispatch, isEnabled])
}
